import * as React from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Navbar } from '@/components/navbar'
import { NewsCard } from '@/components/news-card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { DonationSection } from '@/components/donation-section'
import { PurchaseDialog, FreeUserAlert } from '@/components/purchase-dialog'
import { newsApi } from '@/services/api'
import { formatDate, formatNumber, isAuthenticated, getToken, generateUniqueId } from '@/lib/utils'
import { dashboardApi } from '@/services/api'
import { getRandomImage, SOCKET_URL } from '@/lib/config'
import { Calendar, Eye, Share2, Bookmark, ExternalLink, ArrowLeft, ArrowRight, Home, Copy, X, BookmarkCheck, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { toast } from 'sonner'
import io from 'socket.io-client'

function ArticleDetailSkeleton() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Skeleton className="h-8 w-24 mb-6" />
      <Skeleton className="h-12 w-full mb-4" />
      <div className="flex items-center gap-4 mb-6">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1">
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
      <Skeleton className="aspect-video w-full mb-6" />
      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </div>
  )
}

export function ArticleDetailPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [bookmarked, setBookmarked] = React.useState(false)
  const [bookmarkLoading, setBookmarkLoading] = React.useState(false)
  const [shareModalOpen, setShareModalOpen] = React.useState(false)
  const [user, setUser] = React.useState(null)
  const [purchaseModalOpen, setPurchaseModalOpen] = React.useState(false)
  const [purchaseLoading, setPurchaseLoading] = React.useState(false)
  const [purchaseData, setPurchaseData] = React.useState(null)
  const [errorCode, setErrorCode] = React.useState(null)

  // Socket refs
  const socketRef = React.useRef(null)
  const startTimeRef = React.useRef(Date.now())
  const uuid = React.useMemo(() => generateUniqueId(), [])
  const authToken = getToken()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['article', slug],
    queryFn: async () => {
      try {
        return await newsApi.getArticle(slug)
      } catch (err) {
        // Handle 403 errors for purchase/subscription
        if (err.response?.status === 403) {
          const code = err.response?.data?.code
          const msg = err.response?.data?.msg
          console.log('Article access error:', { code, msg, data: err.response?.data })
          setErrorCode(code)
          
          if (code === 40109) {
            // Article requires purchase
            console.log('Purchase required:', err.response?.data?.data)
            setPurchaseData(err.response?.data?.data)
            setPurchaseModalOpen(true)
          } else if (code === 40100) {
            // No subscription
            toast.error('Subscription required')
            setTimeout(() => navigate('/pricing'), 1000)
          } else if (msg === 'FREE_TIER_LIMIT_REACHED') {
            // Free tier daily limit reached
            const reason = err.response?.data?.data?.reason
            if (reason === 'limit_reached') {
              toast.error('Daily free article limit reached. Upgrade for unlimited access!')
            } else if (reason === 'premium_required') {
              toast.error('This article requires a premium subscription')
            }
            setTimeout(() => navigate('/pricing'), 2000)
          }
        }
        throw err
      }
    },
    retry: false,
  })

  const { data: nextPrevData } = useQuery({
    queryKey: ['nextPrev', slug],
    queryFn: () => newsApi.getNextPrev(slug),
    enabled: !!data?.data,
  })

  const article = data?.data
  const nextPrev = nextPrevData?.data

  // Fetch user data for WebSocket
  React.useEffect(() => {
    const fetchUser = async () => {
      if (authToken && !user) {
        try {
          const response = await dashboardApi.getProfile()
          setUser(response.data)
        } catch (error) {
          console.error('Error fetching user:', error)
        }
      }
    }
    fetchUser()
  }, [authToken, user])

  // Update bookmarked state when article loads
  React.useEffect(() => {
    if (article) {
      setBookmarked(article.bookmarked || false)
    }
  }, [article])

  // Socket connection for read time tracking
  React.useEffect(() => {
    if (!article || !authToken) return

    startTimeRef.current = Date.now()

    // Disconnect previous socket if exists
    if (socketRef.current) {
      socketRef.current.disconnect()
    }

    // Connect to socket
    socketRef.current = io(SOCKET_URL, {
      query: { slug },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    })

    socketRef.current.on('connect', () => {
      console.log('Socket connected for read time tracking')
    })

    socketRef.current.on('connect_error', (error) => {
      console.error('Socket connection error:', error)
    })

    // Send read time every second
    const intervalId = setInterval(() => {
      const readTime = Math.floor((Date.now() - startTimeRef.current) / 1000)
      socketRef.current.emit('updateReadTime', {
        reference: uuid,
        article_slug: slug,
        user: user?.alias,
        token: authToken,
        slug,
        max_read_time: readTime,
        feeder: article.feeder?.alias || article.feeder?.slug,
      })
    }, 1000)

    // Cleanup
    return () => {
      clearInterval(intervalId)
      if (socketRef.current) {
        const totalReadTime = Math.floor((Date.now() - startTimeRef.current) / 1000)
        socketRef.current.emit('userDisconnected', {
          reference: uuid,
          article_slug: slug,
          user: user?.alias,
          token: authToken,
          slug,
          max_read_time: totalReadTime,
          feeder: article.feeder?.alias || article.feeder?.slug,
        })
        socketRef.current.disconnect()
      }
    }
  }, [article, slug, authToken, user, uuid])

  // Handle page visibility for read time tracking
  React.useEffect(() => {
    let hasSentTimeSpent = false

    const sendTimeSpent = () => {
      if (hasSentTimeSpent) return
      if (socketRef.current && article) {
        const readTime = Math.floor((Date.now() - startTimeRef.current) / 1000)
        socketRef.current.emit('userDisconnected', {
          reference: uuid,
          article_slug: slug,
          user: user?.alias,
          token: authToken,
          slug,
          max_read_time: readTime,
          feeder: article.feeder?.alias || article.feeder?.slug,
        })
      }
      hasSentTimeSpent = true
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendTimeSpent()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', sendTimeSpent)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', sendTimeSpent)
    }
  }, [slug, authToken, user, uuid, article])

  // Handle purchase
  const handlePurchase = async () => {
    if (!isAuthenticated()) {
      localStorage.setItem('ldirect', window.location.pathname)
      navigate('/login')
      return
    }

    setPurchaseLoading(true)
    try {
      const response = await newsApi.purchaseArticle(slug)
      toast.success('Article purchased successfully!')
      setPurchaseModalOpen(false)
      // Refetch article to get full content
      window.location.reload()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed. Please try again.')
    } finally {
      setPurchaseLoading(false)
    }
  }

  // Handle bookmark toggle
  const handleBookmark = async () => {
    if (!isAuthenticated()) {
      localStorage.setItem('ldirect', window.location.pathname)
      navigate('/login')
      return
    }

    const previousState = bookmarked
    setBookmarked(!bookmarked) // Optimistic update
    setBookmarkLoading(true)

    try {
      await newsApi.toggleBookmark(slug)
      toast.success(previousState ? 'Bookmark removed' : 'Bookmark added')
    } catch (error) {
      setBookmarked(previousState) // Revert on error
      toast.error('Failed to update bookmark')
    } finally {
      setBookmarkLoading(false)
    }
  }

  // Handle share
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/posts/${slug}` : ''

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl)
    toast.success('Link copied to clipboard!')
  }

  const shareLinks = article ? {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(article.title + ' ' + shareUrl)}`,
    email: `mailto:?subject=${encodeURIComponent(article.title)}&body=${encodeURIComponent(shareUrl)}`
  } : {}

  // IMPORTANT: All hooks must be called BEFORE any conditional returns
  // Image URL logic matching scrollne.ws
  const imageUrl = React.useMemo(() => {
    if (!article) return null
    const relatedImages = article.after_at || []

    // Priority: resized_featured_image > alt_img > thumbnail > related images
    if (article.resized_featured_image) {
      return article.resized_featured_image
    } else if (article.alt_img) {
      return article.alt_img
    } else if (article.thumbnail) {
      return article.thumbnail
    } else if (relatedImages.length > 0) {
      const sortedImages = [...relatedImages].sort((a, b) => a.after_at - b.after_at)
      return sortedImages[0]?.featured_image
    }
    return null // Don't show placeholder image
  }, [article])

  // Track if we're using an after_at image as featured image (to avoid duplicates)
  const usedFirstAfterAtImage = React.useMemo(() => {
    if (!article) return false
    const relatedImages = article.after_at || []

    // If we don't have resized_featured_image, alt_img, or thumbnail, 
    // and we're using the first after_at image as featured
    if (!article.resized_featured_image && !article.alt_img && !article.thumbnail && relatedImages.length > 0) {
      return true
    }
    return false
  }, [article])

  const imageCaption = React.useMemo(() => {
    if (!article) return null
    const relatedImages = article.after_at || []

    if (article.resized_featured_image && article.caption) {
      return article.caption
    }

    if (relatedImages.length > 0) {
      const sortedImages = [...relatedImages].sort((a, b) => a.after_at - b.after_at)
      return sortedImages[0]?.caption
    }
    return null
  }, [article])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <ArticleDetailSkeleton />
      </div>
    )
  }

  if (isError || !article) {
    // Show purchase dialog if error code is 40109
    console.log('Error state:', { isError, errorCode, purchaseData, purchaseModalOpen })

    if (errorCode === 40109 && purchaseData) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <Navbar />
          <PurchaseDialog
            articlePrice={purchaseData.article_price || 0.29}
            originalUrl={purchaseData.original_url}
            open={purchaseModalOpen}
            onClose={() => {
              setPurchaseModalOpen(false)
              navigate('/')
            }}
            onPurchase={handlePurchase}
            loading={purchaseLoading}
          />
          <div className="max-w-4xl mx-auto px-4 py-16 text-center">
            <div className="bg-primary/10 rounded-full p-8 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
              <svg className="w-12 h-12 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Premium Article</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">This article requires a one-time purchase to read.</p>
            <Button onClick={() => setPurchaseModalOpen(true)}>
              Purchase Article
            </Button>
          </div>
        </div>
      )
    }

    // Regular error
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="bg-red-50 dark:bg-red-900/20 rounded-full p-8 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
            <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Article Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">The article you're looking for doesn't exist or has been removed.</p>
          <Link to="/">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-4xl mx-auto px-4 py-8"
      >
        {/* Article header */}
        <header className="mb-8">
          {/* Mobile Top Navigation */}
          <div className="flex md:hidden items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <Link
                to="/"
                className="flex items-center justify-center w-10 h-10 bg-white border border-gray-200 text-gray-700 hover:text-primary hover:border-primary/30 rounded-full shadow-sm transition-all"
                aria-label="Home"
              >
                <Home className="w-5 h-5" />
              </Link>
              {nextPrev && (nextPrev.feed?.previous_article || nextPrev.firehose?.previous_article) && (
                <Link
                  to={`/posts/${(nextPrev.feed?.previous_article || nextPrev.firehose?.previous_article).slug}`}
                  className="flex items-center gap-2 px-4 h-10 bg-white border border-gray-200 text-gray-700 hover:text-primary hover:border-primary/30 rounded-full shadow-sm transition-all text-sm font-medium"
                  aria-label="Previous"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Prev
                </Link>
              )}
            </div>

            {nextPrev && (nextPrev.feed?.next_article || nextPrev.firehose?.next_article) && (
              <Link
                to={`/posts/${(nextPrev.feed?.next_article || nextPrev.firehose?.next_article).slug}`}
                className="flex items-center gap-2 px-4 h-10 bg-gray-900 border border-gray-900 text-white hover:bg-gray-800 rounded-full shadow-sm transition-all text-sm font-medium"
                aria-label="Next"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>

          {/* Desktop Layout */}
          <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-8 mb-6 relative">
            {/* Left Nav (Desktop) */}
            <div className="hidden md:flex items-center gap-6 mt-3 shrink-0 absolute -left-40 top-0 xl:-left-48">
              <Link to="/" className="p-2 border border-dashed border-gray-400 text-gray-900 hover:text-primary transition-colors hover:scale-105" aria-label="Home">
                <Home className="w-6 h-6" />
              </Link>
              {nextPrev && (nextPrev.feed?.previous_article || nextPrev.firehose?.previous_article) ? (
                <Link
                  to={`/posts/${(nextPrev.feed?.previous_article || nextPrev.firehose?.previous_article).slug}`}
                  className="text-gray-900 hover:text-primary transition-all hover:-translate-x-2"
                  aria-label="Previous Article"
                >
                  <ArrowLeft className="w-8 h-8" strokeWidth={2.5} />
                </Link>
              ) : (
                <div className="w-8 h-8" />
              )}
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight flex-1">
              {article.title}
            </h1>

            {/* Right Nav (Desktop) */}
            <div className="hidden md:flex items-center justify-end mt-3 shrink-0 absolute -right-32 top-0 xl:-right-40">
              {nextPrev && (nextPrev.feed?.next_article || nextPrev.firehose?.next_article) ? (
                <Link
                  to={`/posts/${(nextPrev.feed?.next_article || nextPrev.firehose?.next_article).slug}`}
                  className="text-gray-900 hover:text-primary transition-all hover:translate-x-2"
                  aria-label="Next Article"
                >
                  <ArrowRight className="w-8 h-8" strokeWidth={2.5} />
                </Link>
              ) : (
                <div className="w-8 h-8" />
              )}
            </div>
          </div>

          {/* Provider info */}
          <div className="flex items-center gap-4 mb-6">
            {article.feeder?.logo && (
              <img
                src={article.feeder.logo}
                alt={article.feeder.display_name || article.feeder.name}
                className="w-12 h-12 rounded-full"
              />
            )}
            <div className="flex-1">
              <Link
                to={`/provider/${article.feeder?.slug}`}
                className="font-semibold text-gray-900 hover:text-primary transition-colors"
              >
                {article.feeder?.display_name || article.feeder?.name}
              </Link>
              <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(article.pub_date)}
                </span>
                {article.author && (
                  <>
                    <span>•</span>
                    <span>By {article.author.name || article.author}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBookmark}
              disabled={bookmarkLoading}
              className={bookmarked ? 'bg-primary/10 border-primary text-primary' : ''}
            >
              {bookmarkLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : bookmarked ? (
                <BookmarkCheck className="w-4 h-4 mr-2" />
              ) : (
                <Bookmark className="w-4 h-4 mr-2" />
              )}
              {bookmarked ? 'Bookmarked' : 'Bookmark'}
            </Button>
            {/* Share button removed */}
          </div>
        </header>

        {/* Featured image - only show for non-automated articles */}
        {/* For automated/from_feed articles, images are already in the HTML content */}
        {imageUrl && !article.feeder?.automated && !article.feeder?.from_feed && (
          <div className="mb-8">
            <figure>
              <img
                src={imageUrl}
                alt={article.title}
                className="w-full rounded-xl shadow-lg"
                loading="lazy"
              />
              {imageCaption && imageCaption !== "No Caption" && (
                <figcaption className="text-sm text-gray-600 mt-2 text-center italic">
                  {imageCaption}
                </figcaption>
              )}
            </figure>
          </div>
        )}

        {/* Article content */}
        <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
          <style>{`
            .article-content {
              color: #1a1a1a;
              font-family: Georgia, 'Times New Roman', serif;
            }
            .article-content p {
              margin-bottom: 1rem;
              line-height: 1.75;
              text-align: justify;
            }
            .article-content a {
              color: #0066cc;
              text-decoration: none;
              border-bottom: 1px solid #0066cc;
              transition: color 0.2s ease;
            }
            .article-content a:hover {
              color: #004499;
              border-bottom-color: #004499;
            }
            .article-content h1,
            .article-content h2,
            .article-content h3 {
              margin-top: 1.75rem;
              margin-bottom: 0.75rem;
              font-weight: 700;
              line-height: 1.3;
              color: #000;
            }
            .article-content h4,
            .article-content h5,
            .article-content h6 {
              margin-top: 1.5rem;
              margin-bottom: 0.5rem;
              font-weight: 600;
              line-height: 1.3;
              color: #000;
            }
            .article-content ul,
            .article-content ol {
              margin-bottom: 1rem;
              padding-left: 2rem;
            }
            .article-content li {
              margin-bottom: 0.35rem;
              line-height: 1.7;
            }
            .article-content figure {
              margin: 1.75rem 0;
            }
            .article-content figcaption {
              font-size: 0.875rem;
              color: #666;
              font-style: italic;
              margin-top: 0.5rem;
            }
            .article-content img {
              max-width: 100%;
              height: auto;
            }
            .article-content blockquote {
              border-left: 3px solid #ddd;
              padding-left: 1.25rem;
              margin: 1.5rem 0;
              font-style: italic;
              color: #555;
            }
            .article-content strong {
              font-weight: 600;
            }
            .article-content em {
              font-style: italic;
            }
          `}</style>
          <div className="prose prose-lg max-w-none">
            {article.content ? (
              <>
                {article.feeder?.automated || article.feeder?.from_feed ? (
                  // Automated/from_feed: Render entire content as-is (images already in correct positions)
                  (() => {
                    // Decode HTML entities using textarea for proper decoding
                    const textarea = document.createElement('textarea')
                    textarea.innerHTML = article.content
                    const decodedContent = textarea.value

                    return (
                      <div
                        className="article-content"
                        dangerouslySetInnerHTML={{ __html: decodedContent }}
                        style={{
                          fontSize: '16px',
                          lineHeight: '1.8',
                        }}
                      />
                    )
                  })()
                ) : (
                  // Non-automated: Check if content contains HTML tags
                  (() => {
                    // Decode HTML entities and check if it's HTML or plain text
                    const textarea = document.createElement('textarea')
                    textarea.innerHTML = article.content
                    let decodedContent = textarea.value
                    const hasHTMLTags = /<[^>]+>/.test(decodedContent)

                    if (hasHTMLTags) {
                      // Content has HTML tags, render as HTML
                      return (
                        <div
                          className="article-content"
                          dangerouslySetInnerHTML={{ __html: decodedContent }}
                          style={{
                            fontSize: '16px',
                            lineHeight: '1.8',
                          }}
                        />
                      )
                    } else {
                      // Plain text content with after_at images
                      return (
                        <div className="article-content" style={{ fontSize: '16px', lineHeight: '1.8' }}>
                          {article.content.split('\n').filter(line => line.trim() !== '').map((line, index) => {
                            let afterAtImages = article.after_at?.filter(img => img.after_at === index) || []

                            // IMPORTANT: Skip first after_at image if it's being used as featured image
                            if (usedFirstAfterAtImage && index === 0 && afterAtImages.length > 0) {
                              afterAtImages = afterAtImages.slice(1) // Skip the first image
                            }

                            return (
                              <React.Fragment key={index}>
                                <p className="mb-4">{line}</p>
                                {afterAtImages.map((imgData, imgIndex) => (
                                  <figure key={imgIndex} className="my-6">
                                    <img
                                      src={imgData.featured_image}
                                      alt={imgData.caption || 'Article image'}
                                      className="w-full rounded-lg"
                                      loading="lazy"
                                    />
                                    {imgData.caption && imgData.caption !== 'No Caption' && (
                                      <figcaption className="text-sm text-gray-600 mt-2 text-center italic">
                                        {imgData.caption}
                                      </figcaption>
                                    )}
                                  </figure>
                                ))}
                              </React.Fragment>
                            )
                          })}
                        </div>
                      )
                    }
                  })()
                )}
              </>
            ) : (
              <p className="text-gray-600">
                {(() => {
                  const text = article.description || article.excerpt || 'No content available. Click "Read Original" to view the full article on the source website.'
                  // Strip HTML tags if present
                  if (text.includes('<')) {
                    const div = document.createElement('div')
                    div.innerHTML = text
                    return div.textContent || div.innerText || text
                  }
                  return text
                })()}
              </p>
            )}
          </div>

          {/* View on Original Site - right after content */}
          {(article.link || article.original_url) && (
            <div className="mt-6 pt-4">
              <a
                href={article.link || article.original_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-medium px-4 py-3 rounded-lg transition-all duration-200 hover:shadow-md hover:scale-105 no-underline"
              >
                <ExternalLink className="w-4 h-4" />
                View on Original
              </a>
            </div>
          )}
        </div>

        {/* 2. Tags Section */}
        {article.tags && article.tags.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-gray-700 mr-2">Tags:</span>
              {article.tags.slice(0, 12).map((tag) => (
                <Link key={tag.slug} to={`/tags/${tag.slug}`}>
                  <Badge variant="secondary" className="hover:bg-gray-300 transition-colors text-sm">
                    {tag.name || tag.title}
                  </Badge>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* 3. Donation/Tips Section */}
        {!article?.feeder?.limited_feed && <DonationSection article={article} />}

        {/* Next/Previous Articles */}
        {nextPrev && (nextPrev.feed?.previous_article || nextPrev.feed?.next_article || nextPrev.firehose?.previous_article || nextPrev.firehose?.next_article) && (
          <div className="mt-12 space-y-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Continue Reading</h2>

            {/* User's Feed Navigation */}
            {nextPrev.feed && (nextPrev.feed.previous_article || nextPrev.feed.next_article) && (
              <div>
                <h3 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wide">
                  {isAuthenticated() ? "From Your Feed" : "From State News"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {nextPrev.feed.previous_article && (
                    <Link to={`/posts/${nextPrev.feed.previous_article.slug}`}>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="bg-[#5B883C]/[0.27] p-4 rounded-xl border border-gray-200 hover:shadow-lg transition-all h-full"
                      >
                        <div className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
                          <ChevronLeft className="w-4 h-4" />
                          <span className="text-sm">Previous</span>
                        </div>
                        <h3 className="font-bold text-gray-900 line-clamp-2 text-sm mb-2">
                          {nextPrev.feed.previous_article.title}
                        </h3>
                        <p className="text-xs text-gray-700">
                          {nextPrev.feed.previous_article.feeder?.toUpperCase() || 'Unknown'} • {formatDate(nextPrev.feed.previous_article.pub_date)}
                        </p>
                      </motion.div>
                    </Link>
                  )}

                  {nextPrev.feed.next_article && (
                    <Link to={`/posts/${nextPrev.feed.next_article.slug}`}>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200 hover:shadow-lg transition-all h-full"
                      >
                        <div className="flex items-center justify-end gap-2 text-orange-600 font-semibold mb-2">
                          <span className="text-sm">Next</span>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                        <h3 className="font-bold text-gray-900 line-clamp-2 text-sm mb-2 text-right">
                          {nextPrev.feed.next_article.title}
                        </h3>
                        <p className="text-xs text-gray-700 text-right">
                          {nextPrev.feed.next_article.feeder?.toUpperCase() || 'Unknown'} • {formatDate(nextPrev.feed.next_article.pub_date)}
                        </p>
                      </motion.div>
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* Firehose Navigation */}
            {nextPrev.firehose && (nextPrev.firehose.previous_article || nextPrev.firehose.next_article) && (
              <div>
                <h3 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wide">From Firehose (All Articles)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {nextPrev.firehose.previous_article && (
                    <Link to={`/posts/${nextPrev.firehose.previous_article.slug}`}>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="bg-[#5B883C]/[0.27] p-4 rounded-xl border border-gray-200 hover:shadow-lg transition-all h-full"
                      >
                        <div className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
                          <ChevronLeft className="w-4 h-4" />
                          <span className="text-sm">Previous</span>
                        </div>
                        <h3 className="font-bold text-gray-900 line-clamp-2 text-sm mb-2">
                          {nextPrev.firehose.previous_article.title}
                        </h3>
                        <p className="text-xs text-gray-700">
                          {nextPrev.firehose.previous_article.feeder?.toUpperCase() || 'Unknown'} • {formatDate(nextPrev.firehose.previous_article.pub_date)}
                        </p>
                      </motion.div>
                    </Link>
                  )}

                  {nextPrev.firehose.next_article && (
                    <Link to={`/posts/${nextPrev.firehose.next_article.slug}`}>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200 hover:shadow-lg transition-all h-full"
                      >
                        <div className="flex items-center justify-end gap-2 text-orange-600 font-semibold mb-2">
                          <span className="text-sm">Next</span>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                        <h3 className="font-bold text-gray-900 line-clamp-2 text-sm mb-2 text-right">
                          {nextPrev.firehose.next_article.title}
                        </h3>
                        <p className="text-xs text-gray-700 text-right">
                          {nextPrev.firehose.next_article.feeder?.toUpperCase() || 'Unknown'} • {formatDate(nextPrev.firehose.next_article.pub_date)}
                        </p>
                      </motion.div>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </motion.article>

      {/* Share Modal */}
      <AnimatePresence>
        {shareModalOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setShareModalOpen(false)}
            >
              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md relative"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Share Article</h3>
                  <button
                    onClick={() => setShareModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Copy Link */}
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Share Link
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                    />
                    <Button size="sm" onClick={handleCopyLink}>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </Button>
                  </div>
                </div>

                {/* Social Share Buttons */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 mb-3">Share on social media</p>

                  <a
                    href={shareLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 bg-blue-400 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900">Twitter</span>
                  </a>

                  <a
                    href={shareLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div data-logo-box className="w-8 h-8 bg-primary rounded-full flex items-center justify-center transition-all duration-300">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900">Facebook</span>
                  </a>

                  <a
                    href={shareLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div data-logo-box className="w-8 h-8 bg-primary rounded-full flex items-center justify-center transition-all duration-300">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900">LinkedIn</span>
                  </a>

                  <a
                    href={shareLinks.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900">WhatsApp</span>
                  </a>

                  <a
                    href={shareLinks.email}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900">Email</span>
                  </a>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Scroll to top FAB */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed right-4 bottom-20 md:bottom-4 z-40 p-3 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 group"
        aria-label="Scroll to top"
      >
        <svg
          className="w-5 h-5 group-hover:-translate-y-1 transition-transform duration-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  )
}
